import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { brandingAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Buildings, UploadSimple, Trash, FloppyDisk, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandingPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [offices, setOffices] = useState([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState('');
  const [branding, setBranding] = useState({
    invoice_company_name: '',
    invoice_company_address: '',
    has_logo: false,
    logo_url: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);  // local blob preview after picking a file

  useEffect(() => {
    (async () => {
      if (isSuperAdmin) {
        try {
          const res = await axios.get(`${API_BASE}/offices`, { withCredentials: true });
          setOffices(res.data || []);
          if (res.data?.[0]?.id) setSelectedOfficeId(res.data[0].id);
        } catch { toast.error('Failed to load offices'); }
      } else if (user?.office_id) {
        setSelectedOfficeId(user.office_id);
      } else { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => { if (selectedOfficeId) load(selectedOfficeId); }, [selectedOfficeId]);

  const load = async (officeId) => {
    setLoading(true);
    setPreviewUrl(null);
    try {
      const res = await brandingAPI.get(officeId);
      setBranding({
        invoice_company_name: res.data.invoice_company_name || '',
        invoice_company_address: res.data.invoice_company_address || '',
        has_logo: !!res.data.has_logo,
        logo_url: res.data.logo_url,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load branding');
    } finally { setLoading(false); }
  };

  const saveText = async () => {
    setSaving(true);
    try {
      await brandingAPI.update(selectedOfficeId, {
        invoice_company_name: branding.invoice_company_name,
        invoice_company_address: branding.invoice_company_address,
      });
      toast.success('Branding saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Pick an image file (png/jpg/webp)'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5 MB'); return; }
    setPreviewUrl(URL.createObjectURL(f));
    uploadFile(f);
  };

  const uploadFile = async (f) => {
    setUploading(true);
    try {
      await brandingAPI.uploadLogo(selectedOfficeId, f);
      toast.success('Logo uploaded');
      await load(selectedOfficeId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove the current logo? Future invoices will fall back to text-only branding.')) return;
    try {
      await brandingAPI.removeLogo(selectedOfficeId);
      toast.success('Logo removed');
      setPreviewUrl(null);
      load(selectedOfficeId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (!isSuperAdmin && user?.role !== 'supervisor' && user?.role !== 'admin') {
    return <div className="flex flex-col items-center justify-center h-64"><h3 className="font-heading text-lg font-medium">Access Restricted</h3></div>;
  }
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  // The /api/offices/{id}/logo endpoint requires auth cookie, so we use the API path with the full base.
  const logoSrc = previewUrl || (branding.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}?t=${Date.now()}` : null);

  return (
    <div className="space-y-6 fade-in" data-testid="branding-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold">Invoice Branding</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Customize the company name, address, and logo shown on PDF invoices and welcome emails for this office. Each office can have its own branding.
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex flex-col gap-1 min-w-[260px]" data-testid="office-picker">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Office</Label>
            <Select value={selectedOfficeId} onValueChange={setSelectedOfficeId}>
              <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select office" /></SelectTrigger>
              <SelectContent>
                {offices.map(o => (
                  <SelectItem key={o.id} value={o.id}><span className="flex items-center gap-2"><Buildings weight="duotone" className="w-4 h-4" />{o.name}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Logo */}
      <Card className="rounded-2xl border border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Company logo</CardTitle>
          <CardDescription>Shown in the top-left of every invoice PDF. PNG/JPG/WEBP, under 5 MB. Wide rectangular logos look best (~3:1 aspect).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-full md:w-64 h-32 rounded-xl border border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden" data-testid="logo-preview">
              {logoSrc ? (
                <img src={logoSrc} alt="Logo preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="text-center text-muted-foreground text-xs">
                  <ImageIcon weight="duotone" className="w-8 h-8 mx-auto mb-1 opacity-60" />
                  <p>No logo yet</p>
                </div>
              )}
            </div>
            <div className="flex-1 space-y-3">
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onPickFile} data-testid="logo-file-input" />
              <Button className="rounded-xl gap-2" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                <UploadSimple weight="bold" className="w-4 h-4" />
                {uploading ? 'Uploading...' : (branding.has_logo ? 'Replace logo' : 'Upload logo')}
              </Button>
              {branding.has_logo && (
                <Button variant="outline" className="rounded-xl gap-2 text-destructive hover:bg-destructive/10" onClick={removeLogo} data-testid="remove-logo-btn">
                  <Trash weight="bold" className="w-4 h-4" /> Remove logo
                </Button>
              )}
              <p className="text-xs text-muted-foreground pt-1">Tip: a transparent-background PNG works best — the invoice PDF places it on a white header.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Name + Address */}
      <Card className="rounded-2xl border border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Company name &amp; address</CardTitle>
          <CardDescription>This text appears next to the logo on PDFs and in the "From" line of emails. Leave blank to fall back to the office name.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label>Company name (shown on invoices)</Label>
            <Input
              value={branding.invoice_company_name}
              onChange={(e) => setBranding({ ...branding, invoice_company_name: e.target.value })}
              placeholder="e.g. Spring Well Care Services"
              className="rounded-xl"
              data-testid="branding-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Company address (multi-line OK)</Label>
            <Textarea
              value={branding.invoice_company_address}
              onChange={(e) => setBranding({ ...branding, invoice_company_address: e.target.value })}
              placeholder={"123 Main St\nMiami, FL 33101\n(305) 555-1234"}
              rows={4}
              className="rounded-xl"
              data-testid="branding-address-input"
            />
          </div>
          <div className="flex justify-end">
            <Button className="rounded-xl gap-2" onClick={saveText} disabled={saving} data-testid="save-branding-btn">
              <FloppyDisk weight="bold" className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save branding'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandingPage;
